import { useMutation } from "@tanstack/react-query";
import { Form, Input, Select, Spin, message, Col, Row, Card } from "antd";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createGithubConnection,
  fetchGithubBranches,
  fetchGithubFilesList,
  getProjectInfoById,
  pullLatestCommits,
  updateGithubConnection,
} from "../../../api/apiCallManager"; // Ensure these paths are correct
import useGithubConnectionFormData from "../../../components/hooks/useGithubConnectionFormData";
import AccessTo from "../../../components/is-show/AccessTo";
import FormLabel from "../../../components/label/FormLabel";
import { isFederatedLearning } from "../../../helpers/authFunctions";
import { FL } from "../../router/AddRoutesJson";
import { showError, showSuccess } from "../../../helpers/alerts";
import { LOGGER } from "../../../helpers/logger";
import useProjectInfoQuery from "../../../components/hooks/useProjectInfoQuery";
import { useProjectInfoStore } from "../../../zustand/store/projectInfoStore";

const { Option } = Select;

const ConfigureGithubForDataQuality = () => {
  const [form] = Form.useForm();
  const [branchList, setBranchList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState(""); // State for file name
  const [isSubmit, setIsSubmit] = useState(false);
  const { id } = useParams();
  // Query to fetch initial form data
  const { formData, isFormDataLoaded } = useGithubConnectionFormData();
  const { data: projectInfoData } = useProjectInfoQuery(id, getProjectInfoById);
  const { setProjectInfoZustandState, projectInfoZustandState } =
    useProjectInfoStore();
  const createMutation = useMutation({
    mutationFn: createGithubConnection,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("Github submitted successfully!");
        setIsSubmit(true);
      }
    },
  });
  const updateGithubConnectionMutation = useMutation({
    mutationFn: updateGithubConnection,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("Github updated successfully!");
      }
    },
  });

  const fetchBranchesMutation = useMutation({
    mutationFn: fetchGithubBranches,
    onSuccess: (data) => {
      if (data.statusCode == 200) {
        setBranchList(data.body || []);
      } else {
        showError(
          "Failed to load branches. Please check your GitHub URL and token."
        );
      }
    },
    onError: () => {
      showError("Failed to load branches");
    },
  });

  const fetchFilesMutation = useMutation({
    mutationFn: fetchGithubFilesList,
    onSuccess: (data) => {
      setFileList(data.body || []);
    },
    onError: () => {
      showError("Failed to load files");
    },
  });
  const pullLatestMutation = useMutation({
    mutationFn: () =>
      pullLatestCommits({ project_key: projectInfoData?.project_key }),
    onSuccess: (data) => {
      if (data) {
        showSuccess("Pulled Latest Commits successfully!");
      }
    },
    onError: () => {
      showError("Failed to load files");
    },
  });
  const handlePullLatest = () => {
    pullLatestMutation.mutate();
  };
  const onFinish = (values) => {
    // fileName: selectedFileName
    LOGGER({ ...values });
    if (formData?.length !== 0) {
      updateGithubConnectionMutation.mutate({
        ...values,
        record_id: formData[0].record_id,
      });
    } else {
      createMutation.mutate({
        ...values,
        project_key: projectInfoZustandState.project_key,
      });
    }
    // ____OLD______
    // if (formData?.length !== 0) {
    //   updateGithubConnectionMutation.mutate({
    //     ...values,
    //     fileName: selectedFileName,
    //     record_id: formData[0].record_id,
    //     scriptType: "Data_Quality",
    //   });
    // } else {
    //   createMutation.mutate({
    //     ...values,
    //     fileName: selectedFileName,
    //     scriptType: "Data_Quality",
    //   });
    // }
  };

  const handleValuesChange = () => {
    const { repoUrl, accessToken } = form.getFieldsValue();
    if (branchList.length === 0 && repoUrl && accessToken) {
      fetchBranchesMutation.mutate({ repoUrl, accessToken });
    }
  };

  const handleBranchChange = (branchName) => {
    const { repoUrl, accessToken } = form.getFieldsValue();
    if (repoUrl && accessToken && branchName) {
      fetchFilesMutation.mutate({ repoUrl, accessToken, branchName });
    }
  };

  const handleFileChange = (filePath) => {
    const file = fileList?.find((f) => f.path === filePath);
    setSelectedFileName(file?.name || ""); // Set selected file name
  };
  useEffect(() => {
    if (id && isFormDataLoaded && formData.length !== 0) {
      console.log(formData[0], "<-------------");
      const { repoUrl, accessToken, branchName } = formData[0];
      fetchBranchesMutation.mutate({ repoUrl, accessToken });
      fetchFilesMutation.mutate({ repoUrl, accessToken, branchName });
      form.setFieldsValue(formData[0]);
    }
  }, [formData]);
  const onlyEnableFL = !isFederatedLearning();
  return (
    <div className={`p-5`}>
      {/* {JSON.stringify(projectInfoZustandState.project_key)} */}
      {/* {JSON.stringify(
        fetchBranchesMutation.isPending,
        !fetchFilesMutation.isPending
      )}
      =========== */}
      <Spin spinning={!formData}>
        {/* title="Configure Data Quality Script" */}
        <Card bordered={false}>
          <Form
            name="github_form"
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
            form={form}
            autoComplete="off"
            disabled={onlyEnableFL}
          >
            <Form.Item
              name="repoUrl"
              label={<FormLabel> GitHub URL</FormLabel>}
              rules={[
                {
                  type: "url",
                  required: true,
                  message: "Please input the GitHub URL!",
                },
              ]}
            >
              <Input
                autoComplete="new-password"
                onChange={handleValuesChange}
                placeholder="Enter GitHub Repository URL"
              />
            </Form.Item>
            <Form.Item
              name="accessToken"
              label={<FormLabel> Access Token</FormLabel>}
              rules={[
                {
                  required: true,
                  message: "Please input the Access Token!",
                },
              ]}
            >
              <Input.Password
                autoComplete="new-password"
                onChange={handleValuesChange}
                placeholder="Enter GitHub Access Token"
              />
            </Form.Item>
            <Form.Item
              name="branchName"
              label={<FormLabel> Branch Name</FormLabel>}
              rules={[
                {
                  required: true,
                  message: "Please select a branch!",
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select a branch"
                loading={fetchBranchesMutation.isPending}
                notFoundContent={
                  fetchBranchesMutation.isPending
                    ? "Loading..."
                    : "No branches found"
                }
                disabled={
                  fetchBranchesMutation.isPending ||
                  branchList.length === 0 ||
                  onlyEnableFL
                }
                onChange={handleBranchChange}
              >
                {branchList?.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="dataQualityFolder"
                  label={
                    <FormLabel>Select Data Processing Scripts Folder</FormLabel>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please select a folder!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select a folder"
                    loading={fetchFilesMutation.isPending}
                    notFoundContent={
                      fetchFilesMutation.isPending
                        ? "Loading..."
                        : "No files found"
                    }
                    disabled={
                      fetchFilesMutation.isPending ||
                      fileList?.length === 0 ||
                      onlyEnableFL
                    }
                    onChange={handleFileChange} // Set selected file name
                  >
                    {fileList?.map((file) => (
                      <Option key={file.path} value={file.path}>
                        {file.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="modelTrainingFolder"
                  label={<FormLabel>Select Training Scripts Folder</FormLabel>}
                  rules={[
                    {
                      required: true,
                      message: "Please select a folder!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select a folder"
                    loading={fetchFilesMutation.isPending}
                    notFoundContent={
                      fetchFilesMutation.isPending
                        ? "Loading..."
                        : "No files found"
                    }
                    disabled={
                      fetchFilesMutation.isPending ||
                      fileList?.length === 0 ||
                      onlyEnableFL
                    }
                    onChange={handleFileChange} // Set selected file name
                  >
                    {fileList?.map((file) => (
                      <Option key={file.path} value={file.path}>
                        {file.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {/* {projectInfoData?.projectStatus == "Ready to Train" ||
            projectInfoData?.projectStatus == "Completed" ? (
              ""
            ) : ( */}
            <AccessTo role={[FL]}>
              <div className="flex justify-center  mt-3">
                {formData?.length > 0 && (
                  <button
                    role="button"
                    type="button"
                    onClick={handlePullLatest}
                    disabled={pullLatestMutation.isPending}
                    className="bg-[#6e6e6e] py-2 mt-1 me-3 px-4 rounded-sm text-[13px] w-[50%] text-white"
                  >
                    Pull
                  </button>
                )}
                <button
                  type="submit"
                  disabled={
                    isSubmit || updateGithubConnectionMutation.isPending
                  }
                  className="bg-[#0057B8] py-2 mt-1 px-4 rounded-sm text-[13px] w-[50%] text-white"
                >
                  {formData?.length == 0 ? "Submit" : "Update"}
                </button>
              </div>
            </AccessTo>
            {/* )} */}
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default ConfigureGithubForDataQuality;
